import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  IconButton,
  Typography,
  Box,
  MenuItem,
  InputAdornment,
  Alert,
  FormHelperText
} from '@mui/material';

import { Vehicle } from './FleetMenu';
import NumberField from '../../common/NumberField';
import { OperatingPeriod, BreakPeriod } from '../../../types/simulation';
import { logEvent } from '../../../services/analytics';
import { 
  isValidTimeFormat, 
  validateTimeRange, 
  doTimeRangesOverlap,
  parseTimeInput,
  formatTimeForDisplay,
  extractTimeFromDateTime,
  combineDateWithTime
} from '../../../utils/dateTimeUtils';
import { DEFAULT_SIMULATION_DATE } from '../../../config/constants';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../../utils/iconFallbacks';
interface PointOption {
  id: string;
  name: string;
}

interface FleetVehicleModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (vehicle: Vehicle) => void;
  vehicle?: Vehicle | null;
  points: PointOption[];
  defaultName?: string;
}

const emptyPeriod: OperatingPeriod = {
  start: '',
  end: '',
  breaks: [],
};

const emptyBreak: BreakPeriod = {
  start: '',
  end: '',
  location: '',
};

const FleetVehicleModal: React.FC<FleetVehicleModalProps> = ({ open, onClose, onSave, vehicle, points, defaultName }) => {
  const { t } = useTranslation(['mapCore', 'common']);
  const [name, setName] = useState('');
  const [standardSeats, setStandardSeats] = useState(0);
  const [wheelchairSeats, setWheelchairSeats] = useState(0);
  const [startLocation, setStartLocation] = useState('');
  const [endLocation, setEndLocation] = useState('');
  const [operatingHours, setOperatingHours] = useState<OperatingPeriod[]>([ { ...emptyPeriod } ]);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [allowFullDates, setAllowFullDates] = useState(false);
  const [numberOfVehicles, setNumberOfVehicles] = useState(1);
  // Store original operating hours (time-only) for conversion
  const originalOperatingHoursRef = useRef<OperatingPeriod[]>([]);

  // Get serviceDate from vehicle metadata (for imported vehicles)
  const getServiceDate = (): string | undefined => {
    return vehicle?.uatMetadata?.serviceDate;
  };

  // Convert time-only to full datetime or vice versa based on allowFullDates
  const convertOperatingHours = (hours: OperatingPeriod[], toFullDateTime: boolean, serviceDate?: string): OperatingPeriod[] => {
    if (!toFullDateTime || !serviceDate) {
      // Convert to time-only: extract time from datetime strings
      return hours.map(period => {
        const convertToTime = (val: string): string => {
          if (!val) return '';
          if (isValidTimeFormat(val)) {
            // Already time-only
            return val;
          }
          // Extract time from datetime
          try {
            return extractTimeFromDateTime(val);
          } catch {
            return val;
          }
        };
        
        return {
          ...period,
          start: convertToTime(period.start),
          end: convertToTime(period.end),
          breaks: (period.breaks || []).map(breakPeriod => ({
            ...breakPeriod,
            start: convertToTime(breakPeriod.start),
            end: convertToTime(breakPeriod.end),
          }))
        };
      });
    } else {
      // Convert to full datetime: combine time with serviceDate
      const dateStr = serviceDate.split('T')[0];
      return hours.map(period => {
        const convertToDateTime = (timeStr: string): string => {
          if (!timeStr) return '';
          // If already a full datetime, return as-is
          if (timeStr.includes('T') || timeStr.match(/^\d{4}-\d{2}-\d{2}/)) {
            return timeStr;
          }
          // If it's time-only (HH:mm), combine with serviceDate
          if (isValidTimeFormat(timeStr)) {
            try {
              return combineDateWithTime(dateStr, timeStr);
            } catch (error) {
              console.warn('[FleetVehicleModal] Failed to combine date with time:', error);
              return timeStr;
            }
          }
          return timeStr;
        };
        
        return {
          ...period,
          start: convertToDateTime(period.start),
          end: convertToDateTime(period.end),
          breaks: (period.breaks || []).map(breakPeriod => ({
            ...breakPeriod,
            start: convertToDateTime(breakPeriod.start),
            end: convertToDateTime(breakPeriod.end),
          }))
        };
      });
    }
  };

  // Load vehicle data when vehicle or modal opens
  useEffect(() => {
    if (vehicle) {
      setName(vehicle.name);
      setStandardSeats(vehicle.standardSeats);
      setWheelchairSeats(vehicle.wheelchairSeats);
      setStartLocation(vehicle.startLocation);
      setEndLocation(vehicle.endLocation);
      setNumberOfVehicles(vehicle.numberOfVehicles || 1);
      
      // Get serviceDate from vehicle metadata (from imported fleet)
      const serviceDate = getServiceDate();
      
      // Store original operating hours (time-only format)
      const originalHours = vehicle.operatingHours.length ? vehicle.operatingHours : [{ ...emptyPeriod }];
      originalOperatingHoursRef.current = originalHours;
      
      // If vehicle has serviceDate in metadata, auto-enable full dates mode
      // This ensures imported vehicles show their full date+time
      if (serviceDate) {
        setAllowFullDates(true);
        // Convert time-only operating hours to full datetime using serviceDate
        const convertedHours = convertOperatingHours(originalHours, true, serviceDate);
        setOperatingHours(convertedHours);
      } else {
        // No serviceDate, use operating hours as-is
        setOperatingHours(originalHours);
        setAllowFullDates(false);
      }
    } else {
      setName(defaultName || t('fleetVehicleModal.defaultVehicleName'));
      setStandardSeats(0);
      setWheelchairSeats(0);
      setStartLocation('');
      setEndLocation('');
      setOperatingHours([{ ...emptyPeriod }]);
      setNumberOfVehicles(1);
      setAllowFullDates(false);
    }
    setValidationErrors([]);
  }, [vehicle, open, defaultName]);

  // Convert operating hours when allowFullDates is toggled by user
  const prevAllowFullDatesRef = useRef<boolean | undefined>(undefined);
  useEffect(() => {
    // Skip on initial load (handled in vehicle load effect)
    if (prevAllowFullDatesRef.current === undefined) {
      prevAllowFullDatesRef.current = allowFullDates;
      return;
    }
    
    // Only convert if allowFullDates actually changed (user toggled checkbox)
    if (prevAllowFullDatesRef.current !== allowFullDates && vehicle) {
      const serviceDate = getServiceDate();
      // Convert from original time-only format based on new allowFullDates state
      const convertedHours = convertOperatingHours(
        originalOperatingHoursRef.current.length > 0 
          ? originalOperatingHoursRef.current 
          : operatingHours,
        allowFullDates,
        serviceDate || undefined
      );
      setOperatingHours(convertedHours);
      prevAllowFullDatesRef.current = allowFullDates;
    }
  }, [allowFullDates]);

  // Validation functions
  const validateOperatingHours = (): string[] => {
    const errors: string[] = [];
    
    operatingHours.forEach((period: OperatingPeriod, periodIdx: number) => {
      // Validate period times
      if (!period.start || !period.end) {
        errors.push(t('fleetVehicleModal.errors.periodTimesRequired', { index: periodIdx + 1 }));
        return;
      }

      if (!isValidTimeFormat(period.start)) {
        errors.push(t('fleetVehicleModal.errors.periodInvalidStartFormat', { index: periodIdx + 1 }));
      }

      if (!isValidTimeFormat(period.end)) {
        errors.push(t('fleetVehicleModal.errors.periodInvalidEndFormat', { index: periodIdx + 1 }));
      }

      if (isValidTimeFormat(period.start) && isValidTimeFormat(period.end)) {
        if (!validateTimeRange(period.start, period.end)) {
          errors.push(t('fleetVehicleModal.errors.periodEndAfterStart', { index: periodIdx + 1 }));
        }
      }

      // Validate breaks within this period
      period.breaks?.forEach((breakPeriod: BreakPeriod, breakIdx: number) => {
        if (!breakPeriod.start || !breakPeriod.end) {
          errors.push(t('fleetVehicleModal.errors.breakTimesRequired', { periodIndex: periodIdx + 1, breakIndex: breakIdx + 1 }));
          return;
        }

        if (!isValidTimeFormat(breakPeriod.start)) {
          errors.push(t('fleetVehicleModal.errors.breakInvalidStartFormat', { periodIndex: periodIdx + 1, breakIndex: breakIdx + 1 }));
        }

        if (!isValidTimeFormat(breakPeriod.end)) {
          errors.push(t('fleetVehicleModal.errors.breakInvalidEndFormat', { periodIndex: periodIdx + 1, breakIndex: breakIdx + 1 }));
        }

        if (isValidTimeFormat(breakPeriod.start) && isValidTimeFormat(breakPeriod.end)) {
          if (!validateTimeRange(breakPeriod.start, breakPeriod.end)) {
            errors.push(t('fleetVehicleModal.errors.breakEndAfterStart', { periodIndex: periodIdx + 1, breakIndex: breakIdx + 1 }));
          }

          // Check if break is within operating period
          if (isValidTimeFormat(period.start) && isValidTimeFormat(period.end)) {
            if (breakPeriod.start < period.start || breakPeriod.end > period.end) {
              errors.push(t('fleetVehicleModal.errors.breakWithinPeriod', { periodIndex: periodIdx + 1, breakIndex: breakIdx + 1, start: period.start, end: period.end }));
            }
          }
        }
      });

      // Check for overlapping breaks within the same period
      if (period.breaks && period.breaks.length > 1) {
        for (let i = 0; i < period.breaks.length; i++) {
          for (let j = i + 1; j < period.breaks.length; j++) {
            const break1 = period.breaks[i];
            const break2 = period.breaks[j];

            if (isValidTimeFormat(break1.start) && isValidTimeFormat(break1.end) &&
                isValidTimeFormat(break2.start) && isValidTimeFormat(break2.end)) {
              if (doTimeRangesOverlap(break1.start, break1.end, break2.start, break2.end)) {
                errors.push(t('fleetVehicleModal.errors.breaksOverlap', { periodIndex: periodIdx + 1, break1: i + 1, break2: j + 1 }));
              }
            }
          }
        }
      }
    });
    
    return errors;
  };

  const handleAddPeriod = () => {
    logEvent({
      category: 'Fleet Management',
      action: 'OperatingPeriodAdded',
      label: name || 'Unnamed Vehicle',
      vehicle_id: vehicle?.id || 'new',
      periods_count: operatingHours.length + 1
    });
    setOperatingHours([...operatingHours, { ...emptyPeriod }]);
  };

  const handleRemovePeriod = (idx: number) => {
    logEvent({
      category: 'Fleet Management',
      action: 'OperatingPeriodRemoved',
      label: name || 'Unnamed Vehicle',
      vehicle_id: vehicle?.id || 'new',
      periods_count: operatingHours.length - 1
    });
    setOperatingHours(operatingHours.filter((_, i) => i !== idx));
  };

  const handlePeriodChange = (idx: number, field: keyof OperatingPeriod, value: any) => {
    let processedValue = value;
    
    // If it's a time field, try to parse and validate it
    if (field === 'start' || field === 'end') {
      try {
        processedValue = parseTimeInput(value);
      } catch (error) {
        // If parsing fails, keep the raw value for now (validation will catch it)
        processedValue = value;
      }
    }
    
    setOperatingHours(operatingHours.map((period, i) =>
      i === idx ? { ...period, [field]: processedValue } : period
    ));
  };

  const handleAddBreak = (periodIdx: number) => {
    logEvent({
      category: 'Fleet Management',
      action: 'BreakPeriodAdded',
      label: name || 'Unnamed Vehicle',
      vehicle_id: vehicle?.id || 'new',
      period_index: periodIdx,
      breaks_count: (operatingHours[periodIdx].breaks?.length || 0) + 1
    });
    setOperatingHours(operatingHours.map((period, i) =>
      i === periodIdx
        ? { ...period, breaks: [...(period.breaks || []), { ...emptyBreak }] }
        : period
    ));
  };

  const handleRemoveBreak = (periodIdx: number, breakIdx: number) => {
    logEvent({
      category: 'Fleet Management',
      action: 'BreakPeriodRemoved',
      label: name || 'Unnamed Vehicle',
      vehicle_id: vehicle?.id || 'new',
      period_index: periodIdx,
      break_index: breakIdx
    });
    setOperatingHours(operatingHours.map((period, i) =>
      i === periodIdx
        ? { ...period, breaks: (period.breaks || []).filter((_: BreakPeriod, j: number) => j !== breakIdx) }
        : period
    ));
  };

  const handleBreakChange = (periodIdx: number, breakIdx: number, field: keyof BreakPeriod, value: any) => {
    let processedValue = value;
    
    // If it's a time field, try to parse and validate it
    if (field === 'start' || field === 'end') {
      try {
        processedValue = parseTimeInput(value);
      } catch (error) {
        // If parsing fails, keep the raw value for now (validation will catch it)
        processedValue = value;
      }
    }
    
    setOperatingHours(operatingHours.map((period, i) =>
      i === periodIdx
        ? {
            ...period,
            breaks: (period.breaks || []).map((b: BreakPeriod, j: number) =>
              j === breakIdx ? { ...b, [field]: processedValue } : b
            ),
          }
        : period
    ));
  };

  const handleSave = () => {
    // Validate before saving
    const errors = validateOperatingHours();
    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }
    
    // Additional validation
    if (!name.trim()) {
      setValidationErrors([t('fleetVehicleModal.errors.vehicleNameRequired')]);
      return;
    }

    if (standardSeats + wheelchairSeats <= 0) {
      setValidationErrors([t('fleetVehicleModal.errors.atLeastOneSeat')]);
      return;
    }
    
    logEvent({
      category: 'Fleet Management',
      action: 'VehicleSaveAttempted',
      label: name || 'Unnamed Vehicle',
      value: standardSeats + wheelchairSeats,
      is_edit: !!vehicle,
      vehicle_id: vehicle?.id || 'new',
      standard_seats: standardSeats,
      wheelchair_seats: wheelchairSeats,
      has_start_location: !!startLocation,
      has_end_location: !!endLocation,
      operating_periods_count: operatingHours.length,
      has_breaks: operatingHours.some(period => period.breaks && period.breaks.length > 0)
    });

    // Process operatingHours and breaks according to allowFullDates
    const processedOperatingHours = operatingHours.map(period => {
      const processField = (val: string) => {
        if (!val) return '';
        if (allowFullDates) return val;
        // If time-only mode, check if it's already a time string or needs extraction
        if (isValidTimeFormat(val)) {
          // Already a time string, return as is
          return val;
        } else {
          // Full datetime string, extract time portion
          return extractTimeFromDateTime(val);
        }
      };
      return {
        ...period,
        start: processField(period.start),
        end: processField(period.end),
        breaks: (period.breaks || []).map(b => ({
          ...b,
          start: processField(b.start),
          end: processField(b.end),
        }))
      };
    });

    onSave({
      id: vehicle?.id || `${Date.now()}`,
      name,
      standardSeats,
      wheelchairSeats,
      startLocation,
      endLocation,
      operatingHours: processedOperatingHours,
      numberOfVehicles,
    });
    setName(defaultName || t('fleetVehicleModal.defaultVehicleName'));
    setStandardSeats(0);
    setWheelchairSeats(0);
    setStartLocation('');
    setEndLocation('');
    setOperatingHours([{ ...emptyPeriod }]);
    setNumberOfVehicles(1);
    setValidationErrors([]);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth sx={{ zIndex: 2500 }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Icon size={20} name="car-side" />
        {vehicle ? t('fleetVehicleModal.editTitle') : t('fleetVehicleModal.addTitle')}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          {/* Checkbox for full date mode */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <input
                type="checkbox"
                id="allow-full-dates"
                checked={allowFullDates}
                onChange={e => setAllowFullDates(e.target.checked)}
                style={{ marginRight: 8 }}
              />
              <Icon name="gear" size={20} />
              <label htmlFor="allow-full-dates" style={{ fontWeight: 500 }}>
                {t('fleetVehicleModal.allowFullDatesLabel')}
              </label>
            </Box>
            <Typography variant="caption" color="textSecondary" display="block">
              {t('fleetVehicleModal.allowFullDatesHelper')}
            </Typography>
          </Box>

          {validationErrors.length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>{t('fleetVehicleModal.fixErrorsHeading')}</Typography>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                {validationErrors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </Alert>
          )}
          
          <TextField
            label={t('fleetVehicleModal.vehicleName')}
            value={name}
            onChange={e => setName(e.target.value)} 
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Icon size={20} name="car-side" />
                </InputAdornment>
              )
            }}
          />
          <NumberField
            label={t('fleetVehicleModal.numberOfVehicles')}
            value={numberOfVehicles}
            onChange={v => setNumberOfVehicles(v ?? 1)}
            allowEmpty={false}
            fallback={1}
            min={1}
            integer
            fullWidth
            helperText={t('fleetVehicleModal.numberOfVehiclesHelper')}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Icon size={20} d={ICON_FALLBACKS.Numbers} />
                </InputAdornment>
              )
            }}
          />
          <Stack direction="row" spacing={2}>
            <NumberField
              label={t('fleetVehicleModal.standardSeats')}
              value={standardSeats}
              onChange={v => setStandardSeats(v ?? 0)}
              allowEmpty={false}
              fallback={0}
              min={0}
              integer
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon size={20} name="user" />
                  </InputAdornment>
                )
              }}
            />
            <NumberField
              label={t('fleetVehicleModal.wheelchairSeats')}
              value={wheelchairSeats}
              onChange={v => setWheelchairSeats(v ?? 0)}
              allowEmpty={false}
              fallback={0}
              min={0}
              integer
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon size={20} name="wheelchair-move" />
                  </InputAdornment>
                )
              }}
            />
          </Stack>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              select
              label={t('fleetVehicleModal.startDepot')}
              value={startLocation}
              onChange={e => setStartLocation(e.target.value)}
              fullWidth
              SelectProps={{ MenuProps: { sx: { zIndex: 2600 } } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon size={20} name="location-dot" />
                  </InputAdornment>
                )
              }}
            >
              <MenuItem value="">{t('fleetVehicleModal.none')}</MenuItem>
              {points.map(point => (
                <MenuItem key={point.id} value={point.id}>{point.name}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label={t('fleetVehicleModal.endDepot')}
              value={endLocation}
              onChange={e => setEndLocation(e.target.value)}
              fullWidth
              SelectProps={{ MenuProps: { sx: { zIndex: 2600 } } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon size={20} d={ICON_FALLBACKS.LocationOff} />
                  </InputAdornment>
                )
              }}
            >
              <MenuItem value="">{t('fleetVehicleModal.none')}</MenuItem>
              {points.map(point => (
                <MenuItem key={point.id} value={point.id}>{point.name}</MenuItem>
              ))}
            </TextField>
          </Stack>

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Icon size={20} name="clock" />
              {allowFullDates ? t('fleetVehicleModal.operatingHoursDateTime') : t('fleetVehicleModal.operatingHoursTimeOnly')}
            </Typography>
            <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 1 }}>
              {allowFullDates
                ? t('fleetVehicleModal.operatingHoursHelperDateTime')
                : t('fleetVehicleModal.operatingHoursHelperTimeOnly')}
            </Typography>
            
            {operatingHours.map((period, periodIdx) => (
              <Box key={periodIdx} sx={{ border: '1px solid #eee', borderRadius: 2, p: 2, mb: 2 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <TextField
                    label={t('fleetVehicleModal.startTime')}
                    type={allowFullDates ? 'datetime-local' : 'time'}
                    value={period.start}
                    onChange={e => handlePeriodChange(periodIdx, 'start', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 180 }}
                    inputProps={{ lang: 'en-GB' }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Icon name="clock" size={20} />
                        </InputAdornment>
                      )
                    }}
                  />
                  <TextField
                    label={t('fleetVehicleModal.endTime')}
                    type={allowFullDates ? 'datetime-local' : 'time'}
                    value={period.end}
                    onChange={e => handlePeriodChange(periodIdx, 'end', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 180 }}
                    inputProps={{ lang: 'en-GB' }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Icon name="clock" size={20} />
                        </InputAdornment>
                      )
                    }}
                  />
                  <IconButton onClick={() => handleRemovePeriod(periodIdx)} color="error" title={t('fleetVehicleModal.removePeriod')}>
                    <Icon size={20} name="trash" />
                  </IconButton>
                </Stack>

                {period.start && period.end && isValidTimeFormat(period.start) && isValidTimeFormat(period.end) &&
                 !validateTimeRange(period.start, period.end) && (
                  <FormHelperText error sx={{ mt: 1 }}>
                    {t('fleetVehicleModal.endAfterStart')}
                  </FormHelperText>
                )}

                <Typography variant="subtitle2" mt={2} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Icon name="coffee" size={20} />
                  {t('fleetVehicleModal.breaks')}
                </Typography>
                {period.breaks?.map((breakPeriod, breakIdx) => (
                  <Stack key={breakIdx} direction="row" spacing={2} alignItems="center" sx={{ mt: 1 }}>
                    <TextField
                      label={t('fleetVehicleModal.breakStart')}
                      type={allowFullDates ? 'datetime-local' : 'time'}
                      value={breakPeriod.start}
                      onChange={e => handleBreakChange(periodIdx, breakIdx, 'start', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{ width: 180 }}
                      inputProps={{ lang: 'en-GB' }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Icon name="coffee" size={20} />
                          </InputAdornment>
                        )
                      }}
                    />
                    <TextField
                      label={t('fleetVehicleModal.breakEnd')}
                      type={allowFullDates ? 'datetime-local' : 'time'}
                      value={breakPeriod.end}
                      onChange={e => handleBreakChange(periodIdx, breakIdx, 'end', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{ width: 180 }}
                      inputProps={{ lang: 'en-GB' }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Icon name="coffee" size={20} />
                          </InputAdornment>
                        )
                      }}
                    />
                    <TextField
                      select
                      label={t('fleetVehicleModal.breakLocation')}
                      value={breakPeriod.location}
                      onChange={e => handleBreakChange(periodIdx, breakIdx, 'location', e.target.value)}
                      SelectProps={{ MenuProps: { sx: { zIndex: 2600 } } }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Icon name="location-dot" size={20} />
                          </InputAdornment>
                        )
                      }}
                    >
                      <MenuItem value="">{t('fleetVehicleModal.none')}</MenuItem>
                      {points.map(point => (
                        <MenuItem key={point.id} value={point.id}>{point.name}</MenuItem>
                      ))}
                    </TextField>
                    <IconButton onClick={() => handleRemoveBreak(periodIdx, breakIdx)} color="error" title={t('fleetVehicleModal.removeBreak')}>
                      <Icon size={20} name="trash" />
                    </IconButton>
                  </Stack>
                ))}
                <Button startIcon={<Icon size={20} name="plus" />} onClick={() => handleAddBreak(periodIdx)} sx={{ mt: 1 }}>{t('fleetVehicleModal.addBreak')}</Button>
              </Box>
            ))}
            <Button startIcon={<Icon size={20} name="plus" />} onClick={handleAddPeriod}>{t('fleetVehicleModal.addOperatingPeriod')}</Button>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} startIcon={<Icon size={20} name="xmark" />}>{t('common:cancel')}</Button>
        <Button onClick={handleSave} variant="contained" startIcon={<Icon size={20} name="circle-check" />}>{t('fleetVehicleModal.saveVehicle')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default FleetVehicleModal; 
